/**
 * Auth Context
 * Provides authentication state and actions to the application
 */

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { Effect } from "effect";
import type { UserResponse } from "@link-storage/contracts";
import { getAuthState, loginWithGoogle, logout as logoutService, setSessionToken, clearSessionToken } from "../services/authService";

/**
 * Auth context value type
 */
interface AuthContextValue {
  user: UserResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: () => void;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

/**
 * Auth context
 */
const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Auth provider props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth provider component
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Check authentication state
   * @param silent - true이면 isLoading을 변경하지 않아 UI 깜빡임 방지
   */
  const checkAuth = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);
      const authState = await Effect.runPromise(getAuthState());
      setUser(authState.user);
      setIsAuthenticated(authState.isAuthenticated);
    } catch (err) {
      console.error("[AuthContext] Failed to check auth:", err);
      setUser(null);
      setIsAuthenticated(false);
      if (!silent) {
        setError("Failed to check authentication status");
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, []);

  /**
   * Login with Google
   */
  const login = useCallback(() => {
    loginWithGoogle();
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      await Effect.runPromise(logoutService());
      clearSessionToken();
      setUser(null);
      setIsAuthenticated(false);
    } catch (err) {
      console.error("[AuthContext] Failed to logout:", err);
      // Still clear local state even if API fails
      clearSessionToken();
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Refresh auth state
   */
  const refreshAuth = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  /**
   * auth:session-expired 이벤트 수신 시 로컬 상태 초기화
   */
  useEffect(() => {
    const handleSessionExpired = () => {
      clearSessionToken();
      setUser(null);
      setIsAuthenticated(false);
    };
    window.addEventListener("auth:session-expired", handleSessionExpired);
    return () => {
      window.removeEventListener("auth:session-expired", handleSessionExpired);
    };
  }, []);

  /**
   * 탭이 다시 활성화될 때 인증 상태 재확인
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkAuth(true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkAuth]);

  /**
   * Check for session token in URL and store it
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("session_token");

    if (token) {
      // Store token and clear URL
      setSessionToken(token);
      window.history.replaceState({}, "", window.location.pathname);
    }

    // Check auth state
    checkAuth();
  }, [checkAuth]);

  const value: AuthContextValue = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth hook
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
